import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import StatusNotification from '../components/StatusNotification';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Portal,
  Radio,
  RadioGroup,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import TableRowsRoundedIcon from '@mui/icons-material/TableRowsRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import TagRoundedIcon from '@mui/icons-material/TagRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FileUploadRoundedIcon from '@mui/icons-material/FileUploadRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import { useAuth } from '../context/AuthContext';

const LANGUAGES = [
  ['en', 'English'], ['hi', 'Hindi'], ['hi-en', 'Hinglish'], ['bn', 'Bengali'],
  ['mr', 'Marathi'], ['gu', 'Gujarati'], ['ta', 'Tamil'], ['te', 'Telugu'],
  ['kn', 'Kannada'], ['ml', 'Malayalam'], ['pa', 'Punjabi'], ['ur', 'Urdu'],
];

const KEY_PATTERN = /^[a-z0-9][a-z0-9_.-]{0,119}$/;

/** Purpose: manage bot translation keys and localized text. Why: flows and chat experiences can render language-specific content from one environment-scoped catalog. */
export default function Localization() {
  const { selectedBot, selectedEnv, isBotAdmin } = useAuth();
  const [data, setData] = useState({ defaultLanguage: 'en', enabledLanguages: ['en'], entries: [] });
  const [loading, setLoading] = useState(true);
  const [allowedLanguages, setAllowedLanguages] = useState(['en']);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [message, setMessage] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Table view is the most efficient default for multilingual content; users can
  // still switch to the spacious card editor from the toggle.
  const [viewMode, setViewMode] = useState('table');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [copiedKeyIndex, setCopiedKeyIndex] = useState(null);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [languagesDialogOpen, setLanguagesDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importError, setImportError] = useState('');
  const [importMode, setImportMode] = useState('merge');
  const [autoEnableLanguages, setAutoEnableLanguages] = useState(true);
  const fileInputRef = useRef(null);
  const [headerHost, setHeaderHost] = useState(null);
  useEffect(() => { setHeaderHost(document.getElementById('localization-header-actions')); }, []);

  useEffect(() => {
    /** Purpose: Handle handle before unload in the localization module. Why: Keeping this action in one handler makes its state or request update consistent. */
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const canEdit = isBotAdmin(selectedBot) && selectedEnv !== 'prod';
  const nextRowId = useRef(0);
  /** Purpose: Transform with row ids within the localization module. Why: This keeps the surrounding operation consistent with the current interface state and data shape. */
  const withRowIds = useCallback((bundle) => ({
    ...bundle,
    entries: (bundle?.entries || []).map((entry) => ({
      ...entry,
      _rowId: entry?._rowId || `localization-row-${nextRowId.current++}`,
    })),
  }), []);

  /** Purpose: Load load for the localization module. Why: The selected view needs current data to render or continue processing. */
  const load = useCallback(async () => {
    if (!selectedBot) return;
    setLoading(true);
    try {
      const response = await axios.get('/api/v1/localizations', { params: { botId: selectedBot, env: selectedEnv } });
      setData(withRowIds(response.data.localization));
      setAllowedLanguages(response.data.allowedLanguages || ['en']);
      setHasUnsavedChanges(false);
    } catch (error) {
      setMessage({ severity: 'error', text: error.response?.data?.message || 'Could not load localization data' });
    } finally {
      setLoading(false);
    }
  }, [selectedBot, selectedEnv, withRowIds]);

  useEffect(() => { load(); }, [load]);

  /** Purpose: Filter enabled within the localization module. Why: This keeps the surrounding operation consistent with the current interface state and data shape. */
  const enabled = useMemo(() => LANGUAGES.filter(([code]) => data.enabledLanguages.includes(code)), [data.enabledLanguages]);

  /** Purpose: Handle toggle language in the localization module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const toggleLanguage = (code) => {
    setData((current) => {
      const exists = current.enabledLanguages.includes(code);
      if (code === current.defaultLanguage && exists) return current;
      return {
        ...current,
        enabledLanguages: exists
          ? current.enabledLanguages.filter((item) => item !== code)
          : [...current.enabledLanguages, code],
      };
    });
    setHasUnsavedChanges(true);
  };

  /** Purpose: Apply update entry in the localization module. Why: Centralizing this change keeps the relevant state or persisted record consistent. */
  const updateEntry = (index, patch) => {
    setData((current) => ({
      ...current,
      entries: current.entries.map((entry, itemIndex) => itemIndex === index ? { ...entry, ...patch } : entry),
    }));
    setHasUnsavedChanges(true);
  };

  /** Purpose: Apply update value in the localization module. Why: Centralizing this change keeps the relevant state or persisted record consistent. */
  const updateValue = (index, language, value) => {
    setData((current) => ({
      ...current,
      entries: current.entries.map((entry, itemIndex) =>
        itemIndex === index
          ? { ...entry, values: { ...(entry.values || {}), [language]: value } }
          : entry
      ),
    }));
    setHasUnsavedChanges(true);
  };

  /** Purpose: Handle delete entry in the localization module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const deleteEntry = (index) => {
    setData((current) => ({
      ...current,
      entries: current.entries.filter((_, itemIndex) => itemIndex !== index),
    }));
    setHasUnsavedChanges(true);
  };

  /** Purpose: Handle add entry in the localization module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const addEntry = () => {
    const entry = { _rowId: `localization-row-${nextRowId.current++}`, key: '', description: '', values: {} };
    setData((current) => ({ ...current, entries: [entry, ...current.entries] }));
    setHasUnsavedChanges(true);
    if (searchQuery || statusFilter !== 'all') {
      setSearchQuery('');
      setStatusFilter('all');
    }
    if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'test') {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (_) {}
    }
    setMessage({
      severity: 'info',
      text: 'New key added at the top (#1). Type key name and values, then click "Save localization".',
    });
  };

  /** Purpose: Handle copy key to clipboard in the localization module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const copyKeyToClipboard = (key, index) => {
    if (!key) return;
    navigator.clipboard?.writeText(key);
    setCopiedKeyIndex(index);
    setTimeout(() => setCopiedKeyIndex(null), 1800);
  };

  /** Purpose: Handle copy default value to language in the localization module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const copyDefaultValueToLanguage = (index, targetLang) => {
    const defaultValue = data.entries[index]?.values?.[data.defaultLanguage] || '';
    if (defaultValue) {
      updateValue(index, targetLang, defaultValue);
    }
  };

  /** Purpose: Resolve csv escape for the localization module. Why: The surrounding operation uses this value to make its decision. */
  const csvEscape = (val) => {
    const str = val === null || val === undefined ? '' : String(val);
    if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  /** Purpose: Handle export as json in the localization module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const exportAsJson = () => {
    setExportAnchorEl(null);
    const bundle = {
      version: 1,
      botId: selectedBot,
      environment: selectedEnv,
      defaultLanguage: data.defaultLanguage,
      enabledLanguages: data.enabledLanguages,
      exportedAt: new Date().toISOString(),
      entries: data.entries.map((entry) => ({
        key: entry.key.trim().toLowerCase(),
        description: entry.description || '',
        values: entry.values || {},
      })),
    };
    const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const dateStr = new Date().toISOString().slice(0, 10);
    link.download = `localization-${selectedBot || 'bot'}-${selectedEnv || 'dev'}-${dateStr}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  /** Purpose: Handle export as csv in the localization module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const exportAsCsv = () => {
    setExportAnchorEl(null);
    const langCols = data.enabledLanguages;
    const headers = ['Key', 'Description', ...langCols];
    const rows = [headers.map(csvEscape).join(',')];

    data.entries.forEach((entry) => {
      const row = [
        csvEscape(entry.key),
        csvEscape(entry.description || ''),
        ...langCols.map((code) => csvEscape(entry.values?.[code] || '')),
      ];
      rows.push(row.join(','));
    });

    const blob = new Blob([rows.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const dateStr = new Date().toISOString().slice(0, 10);
    link.download = `localization-${selectedBot || 'bot'}-${selectedEnv || 'dev'}-${dateStr}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  /** Purpose: Perform parse csv for the localization module. Why: Callers need a consistent result from this shared operation. */
  const parseCsv = (text) => {
    const cleaned = text.charCodeAt(0) === 0xFEFF ? text.slice(1) : text;
    const rows = [];
    let currentRow = [];
    let currentCell = '';
    let insideQuotes = false;
    for (let i = 0; i < cleaned.length; i++) {
      const char = cleaned[i];
      const nextChar = cleaned[i + 1];
      if (char === '"') {
        if (insideQuotes && nextChar === '"') {
          currentCell += '"';
          i++;
        } else {
          insideQuotes = !insideQuotes;
        }
      } else if (char === ',' && !insideQuotes) {
        currentRow.push(currentCell);
        currentCell = '';
      } else if ((char === '\r' || char === '\n') && !insideQuotes) {
        if (char === '\r' && nextChar === '\n') {
          i++;
        }
        currentRow.push(currentCell);
        currentCell = '';
        if (currentRow.length > 0 && currentRow.some((c) => c.trim() !== '')) {
          rows.push(currentRow);
        }
        currentRow = [];
      } else {
        currentCell += char;
      }
    }
    if (currentCell || currentRow.length > 0) {
      currentRow.push(currentCell);
      if (currentRow.some((c) => c.trim() !== '')) {
        rows.push(currentRow);
      }
    }
    return rows;
  };

  /** Purpose: Load find lang code for the localization module. Why: The selected view needs current data to render or continue processing. */
  const findLangCode = (headerName) => {
    const h = headerName.trim().toLowerCase();
    /** Purpose: Perform match by code for the localization module. Why: Callers need a consistent result from this shared operation. */
    const matchByCode = LANGUAGES.find(([code]) => code.toLowerCase() === h);
    if (matchByCode) return matchByCode[0];
    /** Purpose: Perform match by name for the localization module. Why: Callers need a consistent result from this shared operation. */
    const matchByName = LANGUAGES.find(([, name]) => name.toLowerCase() === h);
    if (matchByName) return matchByName[0];
    return null;
  };

  /** Purpose: Transform process file within the localization module. Why: This keeps the surrounding operation consistent with the current interface state and data shape. */
  const processFile = (file) => {
    setImportError('');
    setImportPreview(null);
    setImportFile({ name: file.name, size: file.size });

    const reader = new FileReader();
    /** Purpose: Provide the reader.onload operation for the localization module. Why: Callers rely on one shared implementation so the derived value or behavior remains consistent. */
    reader.onload = (event) => {
      try {
        const text = event.target?.result || '';
        const isJson = file.name.endsWith('.json') || file.type.includes('json');
        let parsedEntries = [];
        const detectedLangs = new Set();

        if (isJson) {
          const jsonObj = JSON.parse(text);
          if (Array.isArray(jsonObj)) {
            parsedEntries = jsonObj.map((item) => {
              const key = String(item.key || item.id || '').trim().toLowerCase();
              const description = String(item.description || item.desc || '').trim();
              const values = {};
              if (item.values && typeof item.values === 'object') {
                Object.entries(item.values).forEach(([l, val]) => {
                  const lang = l.trim().toLowerCase();
                  values[lang] = String(val ?? '');
                  detectedLangs.add(lang);
                });
              }
              return { key, description, values };
            });
          } else if (jsonObj && typeof jsonObj === 'object') {
            if (Array.isArray(jsonObj.entries)) {
              parsedEntries = jsonObj.entries.map((item) => {
                const key = String(item.key || '').trim().toLowerCase();
                const description = String(item.description || '').trim();
                const values = {};
                if (item.values && typeof item.values === 'object') {
                  Object.entries(item.values).forEach(([l, val]) => {
                    const lang = l.trim().toLowerCase();
                    values[lang] = String(val ?? '');
                    detectedLangs.add(lang);
                  });
                }
                return { key, description, values };
              });
              if (Array.isArray(jsonObj.enabledLanguages)) {
                jsonObj.enabledLanguages.forEach((l) => detectedLangs.add(String(l).trim().toLowerCase()));
              }
            } else {
              parsedEntries = Object.entries(jsonObj).map(([keyRaw, val]) => {
                const key = String(keyRaw).trim().toLowerCase();
                let description = '';
                const values = {};
                if (val && typeof val === 'object' && !Array.isArray(val)) {
                  if (val.values && typeof val.values === 'object') {
                    description = String(val.description || '').trim();
                    Object.entries(val.values).forEach(([l, textVal]) => {
                      const lang = l.trim().toLowerCase();
                      values[lang] = String(textVal ?? '');
                      detectedLangs.add(lang);
                    });
                  } else {
                    Object.entries(val).forEach(([lOrDesc, textVal]) => {
                      if (lOrDesc.toLowerCase() === 'description' || lOrDesc.toLowerCase() === 'desc') {
                        description = String(textVal ?? '');
                      } else {
                        const lang = lOrDesc.trim().toLowerCase();
                        values[lang] = String(textVal ?? '');
                        detectedLangs.add(lang);
                      }
                    });
                  }
                } else if (typeof val === 'string' || typeof val === 'number') {
                  values[data.defaultLanguage] = String(val);
                  detectedLangs.add(data.defaultLanguage);
                }
                return { key, description, values };
              });
            }
          }
        } else {
          // CSV Parsing
          const rows = parseCsv(text);
          if (!rows.length) throw new Error('CSV file is empty');
          const headerRow = rows[0];
          let keyColIdx = -1;
          let descColIdx = -1;
          const langCols = [];

          headerRow.forEach((col, idx) => {
            const clean = col.trim().toLowerCase();
            if (clean === 'key' || clean === 'localization_key' || clean === 'key_name' || clean === 'id') {
              if (keyColIdx === -1) keyColIdx = idx;
            } else if (clean === 'description' || clean === 'desc' || clean === 'note') {
              if (descColIdx === -1) descColIdx = idx;
            } else {
              const langCode = findLangCode(col);
              if (langCode) {
                langCols.push({ idx, langCode });
                detectedLangs.add(langCode);
              }
            }
          });

          if (keyColIdx === -1) keyColIdx = 0;

          for (let r = 1; r < rows.length; r++) {
            const row = rows[r];
            const rawKey = String(row[keyColIdx] || '').trim().toLowerCase();
            if (!rawKey) continue;
            const description = descColIdx !== -1 ? String(row[descColIdx] || '').trim() : '';
            const values = {};
            langCols.forEach(({ idx, langCode }) => {
              if (row[idx] !== undefined) {
                values[langCode] = String(row[idx] || '');
              }
            });
            parsedEntries.push({ key: rawKey, description, values });
          }
        }

        const keyPattern = KEY_PATTERN;
        const validEntries = [];
        let invalidCount = 0;

        parsedEntries.forEach((entry) => {
          const cleanKey = entry.key.trim().toLowerCase();
          if (cleanKey && keyPattern.test(cleanKey)) {
            validEntries.push({ ...entry, key: cleanKey });
          } else {
            invalidCount++;
          }
        });

        if (!validEntries.length) {
          throw new Error('No valid localization keys found in file. Keys must start with a lowercase letter or number and contain only lowercase letters, numbers, _, ., or - (1-120 chars).');
        }

        setImportPreview({
          entries: validEntries,
          validCount: validEntries.length,
          invalidCount,
          totalCount: parsedEntries.length,
          detectedLangs: Array.from(detectedLangs),
        });
      } catch (err) {
        setImportError(err.message || 'Failed to parse file');
      }
    };
    /** Purpose: Provide the reader.onerror operation for the localization module. Why: Callers rely on one shared implementation so the derived value or behavior remains consistent. */
    reader.onerror = () => {
      setImportError('Failed to read the file.');
    };
    reader.readAsText(file);
  };

  /** Purpose: Handle handle file select in the localization module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      processFile(file);
    }
    event.target.value = '';
  };

  /** Purpose: Handle apply import in the localization module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const applyImport = () => {
    if (!importPreview?.entries?.length) return;

    let nextEntries = [];
    let updatedCount = 0;
    let addedCount = 0;

    if (importMode === 'replace') {
      nextEntries = importPreview.entries.map((entry) => ({
        _rowId: `localization-row-${nextRowId.current++}`,
        key: entry.key,
        description: entry.description || '',
        values: entry.values || {},
      }));
      addedCount = nextEntries.length;
    } else {
      // Merge mode
      const entryMap = new Map();
      data.entries.forEach((existing) => {
        entryMap.set(existing.key.toLowerCase(), {
          ...existing,
          values: { ...(existing.values || {}) },
        });
      });

      importPreview.entries.forEach((imported) => {
        const k = imported.key.toLowerCase();
        if (entryMap.has(k)) {
          const existing = entryMap.get(k);
          entryMap.set(k, {
            ...existing,
            description: imported.description || existing.description || '',
            values: {
              ...existing.values,
              ...imported.values,
            },
          });
          updatedCount++;
        } else {
          entryMap.set(k, {
            _rowId: `localization-row-${nextRowId.current++}`,
            key: k,
            description: imported.description || '',
            values: imported.values || {},
          });
          addedCount++;
        }
      });

      nextEntries = Array.from(entryMap.values());
    }

    let nextEnabledLanguages = [...data.enabledLanguages];
    if (autoEnableLanguages && importPreview.detectedLangs?.length) {
      /** Purpose: Resolve to enable for the localization module. Why: The surrounding operation uses this value to make its decision. */
      const toEnable = importPreview.detectedLangs.filter(
        (lang) => allowedLanguages.includes(lang) && !nextEnabledLanguages.includes(lang)
      );
      if (toEnable.length) {
        nextEnabledLanguages = [...nextEnabledLanguages, ...toEnable];
      }
    }

    setData((current) => ({
      ...current,
      enabledLanguages: nextEnabledLanguages,
      entries: nextEntries,
    }));
    setHasUnsavedChanges(true);

    setImportDialogOpen(false);
    setImportFile(null);
    setImportPreview(null);
    setMessage({
      severity: 'success',
      text: importMode === 'replace'
        ? `Replaced with ${addedCount} imported keys. Review changes, then click "Save localization" to persist.`
        : `Successfully imported ${importPreview.entries.length} keys (${updatedCount} updated, ${addedCount} added). Review changes, then click "Save localization" to persist.`,
    });
  };

  /** Purpose: Handle save in the localization module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      /** Purpose: Resolve non empty entries for the localization module. Why: The surrounding operation uses this value to make its decision. */
      const nonEmptyEntries = data.entries.filter((entry) => {
        const hasKey = String(entry.key || '').trim();
        /** Purpose: Perform has values for the localization module. Why: Callers need a consistent result from this shared operation. */
        const hasValues = Object.values(entry.values || {}).some((v) => String(v || '').trim() !== '');
        const hasDesc = String(entry.description || '').trim() !== '';
        return Boolean(hasKey || hasValues || hasDesc);
      });

      /** Purpose: Resolve missing key for the localization module. Why: The surrounding operation uses this value to make its decision. */
      const missingKey = nonEmptyEntries.find((entry) => !String(entry.key || '').trim());
      if (missingKey) {
        setMessage({ severity: 'error', text: 'All entries with translations or descriptions must have a valid Key. Please fill in the missing key or remove the empty row.' });
        setSaving(false);
        return;
      }

      /** Purpose: Resolve invalid key for the localization module. Why: The surrounding operation uses this value to make its decision. */
      const invalidKey = nonEmptyEntries.find((entry) => !KEY_PATTERN.test(String(entry.key || '').trim().toLowerCase()));
      if (invalidKey) {
        setMessage({ severity: 'error', text: `Key "${invalidKey.key}" is invalid. Keys must start with a lowercase letter or number and contain only lowercase letters, numbers, _, -, or . (1-120 chars).` });
        setSaving(false);
        return;
      }

      // Check for duplicate keys before saving
      const seenKeys = new Map();
      for (let i = 0; i < nonEmptyEntries.length; i++) {
        const k = nonEmptyEntries[i].key.trim().toLowerCase();
        if (seenKeys.has(k)) {
          const firstRow = seenKeys.get(k) + 1;
          const secondRow = i + 1;
          setMessage({
            severity: 'error',
            text: `Duplicate key "${k}" found (row #${firstRow} and row #${secondRow}). Each localization key must be unique before saving.`,
          });
          setSaving(false);
          return;
        }
        seenKeys.set(k, i);
      }

      const payload = {
        botId: selectedBot,
        env: selectedEnv,
        defaultLanguage: data.defaultLanguage,
        enabledLanguages: data.enabledLanguages,
        entries: nonEmptyEntries.map((entry) => ({
          key: entry.key.trim().toLowerCase(),
          description: entry.description || '',
          values: entry.values || {},
        })),
      };
      const response = await axios.put('/api/v1/localizations', payload);
      setData(withRowIds(response.data.localization));
      setHasUnsavedChanges(false);
      setMessage({ severity: 'success', text: `Localization saved successfully (${nonEmptyEntries.length} keys). Existing mapped nodes will use these values immediately.` });
    } catch (error) {
      setMessage({ severity: 'error', text: error.response?.data?.message || error.response?.data?.error || 'Could not save localization' });
    } finally {
      setSaving(false);
    }
  };

  /** Purpose: Filter auto translate missing within the localization module. Why: This keeps the surrounding operation consistent with the current interface state and data shape. */
  const autoTranslateMissing = async () => {
    setTranslating(true);
    setMessage(null);
    try {
      /** Purpose: Build next entries within the localization module. Why: This keeps the surrounding operation consistent with the current interface state and data shape. */
      let nextEntries = data.entries.map((entry) => ({ ...entry, values: { ...(entry.values || {}) } }));
      let totalTranslated = 0;
      for (const [targetLanguage] of enabled) {
        if (targetLanguage === data.defaultLanguage || targetLanguage === 'hi-en') continue;
        /** Purpose: Resolve indexes for the localization module. Why: The surrounding operation uses this value to make its decision. */
        const indexes = nextEntries
          .map((entry, index) => ({
            index,
            source: String(entry.values?.[data.defaultLanguage] || '').trim(),
            target: String(entry.values?.[targetLanguage] || '').trim(),
          }))
          .filter((item) => item.source && !item.target);
        if (!indexes.length) continue;

        // Batch in chunks of 50 to avoid request size/provider limits
        const BATCH_SIZE = 50;
        for (let b = 0; b < indexes.length; b += BATCH_SIZE) {
          const batch = indexes.slice(b, b + BATCH_SIZE);
          const response = await axios.post('/api/v1/localizations/translate', {
            botId: selectedBot,
            env: selectedEnv,
            sourceLanguage: data.defaultLanguage,
            targetLanguage,
            texts: batch.map((item) => item.source),
          });
          batch.forEach((item, batchIdx) => {
            const translation = response.data?.translations?.[batchIdx];
            if (translation !== undefined) {
              nextEntries[item.index].values[targetLanguage] = translation;
              totalTranslated++;
            }
          });
        }
      }
      setData((current) => ({ ...current, entries: nextEntries }));
      setHasUnsavedChanges(true);
      setMessage({
        severity: 'success',
        text: totalTranslated > 0
          ? `Auto-translated ${totalTranslated} blank values as a draft. Review changes, then click "Save localization" to persist.`
          : 'No missing translations found to translate.',
      });
    } catch (error) {
      setMessage({ severity: 'error', text: error.response?.data?.message || error.response?.data?.error || 'Automatic translation failed. Check provider configuration.' });
    } finally {
      setTranslating(false);
    }
  };

  // Filter entries based on search and status
  /** Purpose: Provide the filtered entries operation for the localization module. Why: Callers depend on this shared result to keep the surrounding flow consistent. */
  const filteredEntries = useMemo(() => {
    return data.entries.map((entry, originalIndex) => ({ entry, originalIndex })).filter(({ entry }) => {
      const q = searchQuery.trim().toLowerCase();
      const matchesSearch = !q || (
        entry.key.toLowerCase().includes(q) ||
        Object.values(entry.values || {}).some((v) => String(v).toLowerCase().includes(q))
      );
      if (!matchesSearch) return false;

      /** Purpose: Perform is complete for the localization module. Why: Callers need a consistent result from this shared operation. */
      const isComplete = enabled.every(([code]) => Boolean(String(entry.values?.[code] || '').trim()));
      if (statusFilter === 'missing') return !isComplete;
      if (statusFilter === 'complete') return isComplete;
      return true;
    });
  }, [data.entries, searchQuery, statusFilter, enabled]);

  const totalKeys = data.entries.length;
  /** Purpose: Filter missing count within the localization module. Why: This keeps the surrounding operation consistent with the current interface state and data shape. */
  const missingCount = useMemo(() => {
    return data.entries.filter((entry) => !enabled.every(([code]) => Boolean(String(entry.values?.[code] || '').trim()))).length;
  }, [data.entries, enabled]);

  if (!selectedBot) return <Alert severity="info">Select a bot to manage localization.</Alert>;

  const saveButton = (
    <Button
      variant="contained"
      color={hasUnsavedChanges ? 'primary' : 'inherit'}
      startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <SaveRoundedIcon sx={{ fontSize: 16 }} />}
      disabled={!canEdit || saving || loading}
      onClick={save}
      sx={{
        borderRadius: '10px',
        textTransform: 'none',
        fontWeight: 700,
        px: 2,
        height: 34,
        fontSize: '0.8125rem',
        whiteSpace: 'nowrap',
        boxShadow: hasUnsavedChanges ? '0 0 0 2px rgba(59, 130, 246, 0.35)' : undefined,
      }}
    >
      Save localization
    </Button>
  );

  return (
    <Box
      sx={{
        px: 0,
        pt: 0,
        pb: { xs: 3, md: 4 },
        width: '100%',
        minWidth: 0,
        minHeight: '100%',
      }}
    >
        <Portal container={headerHost} disablePortal={!headerHost}>
        <Box
          role="toolbar"
          aria-label="Localization actions"
          display="flex"
          gap={1}
          alignItems="center"
          sx={{
            width: headerHost ? 'max-content' : '100%',
            flexWrap: headerHost ? 'nowrap' : 'wrap',
            mb: headerHost ? 0 : 2,
            '& > .MuiButton-root': {
              borderRadius: '10px',
              height: 34,
              whiteSpace: 'nowrap',
              px: 1.6,
              fontSize: '0.8125rem',
              fontWeight: 600,
            },
          }}
        >
          <Button
            variant="outlined"
            startIcon={<FileUploadRoundedIcon sx={{ fontSize: 16 }} />}
            disabled={!canEdit || loading}
            onClick={() => {
              setImportFile(null);
              setImportPreview(null);
              setImportError('');
              setImportMode('merge');
              setImportDialogOpen(true);
            }}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              px: 1.8,
              height: 34,
              /** Purpose: Derive the border color style for the localization view. Why: Theme and state dependent styling should be calculated from current UI context. */
              borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.15)',
              color: 'text.primary',
              /** Purpose: Derive the bgcolor style for the localization view. Why: Theme and state dependent styling should be calculated from current UI context. */
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#ffffff',
              '&:hover': {
                borderColor: 'primary.main',
                /** Purpose: Derive the bgcolor style for the localization view. Why: Theme and state dependent styling should be calculated from current UI context. */
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
              },
            }}
          >
            Import
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadRoundedIcon sx={{ fontSize: 16 }} />}
            endIcon={<KeyboardArrowDownRoundedIcon sx={{ fontSize: 16 }} />}
            disabled={loading || !data.entries.length}
            onClick={(e) => setExportAnchorEl(e.currentTarget)}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              px: 1.8,
              height: 34,
              /** Purpose: Derive the border color style for the localization view. Why: Theme and state dependent styling should be calculated from current UI context. */
              borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.15)',
              color: 'text.primary',
              /** Purpose: Derive the bgcolor style for the localization view. Why: Theme and state dependent styling should be calculated from current UI context. */
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#ffffff',
              '&:hover': {
                borderColor: 'primary.main',
                /** Purpose: Derive the bgcolor style for the localization view. Why: Theme and state dependent styling should be calculated from current UI context. */
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
              },
            }}
          >
            Export
          </Button>
          <Menu
            anchorEl={exportAnchorEl}
            open={Boolean(exportAnchorEl)}
            onClose={() => setExportAnchorEl(null)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              elevation: 4,
              sx: { borderRadius: 2.5, minWidth: 220, mt: 0.8, p: 0.5 },
            }}
          >
            <MenuItem onClick={exportAsJson} sx={{ py: 1.2, borderRadius: 1.5 }}>
              <Box display="flex" flexDirection="column">
                <Typography variant="body2" fontWeight={700}>Export as JSON</Typography>
                <Typography variant="caption" color="text.secondary">Full structured backup (.json)</Typography>
              </Box>
            </MenuItem>
            <MenuItem onClick={exportAsCsv} sx={{ py: 1.2, borderRadius: 1.5 }}>
              <Box display="flex" flexDirection="column">
                <Typography variant="body2" fontWeight={700}>Export as CSV</Typography>
                <Typography variant="caption" color="text.secondary">Spreadsheet format for Excel/Sheets (.csv)</Typography>
              </Box>
            </MenuItem>
          </Menu>
          <Button
            variant="outlined"
            startIcon={translating ? <CircularProgress size={15} /> : <TranslateRoundedIcon sx={{ fontSize: 16 }} />}
            disabled={!canEdit || translating || loading || !data.entries.length}
            onClick={autoTranslateMissing}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              px: 1.8,
              height: 34,
              /** Purpose: Derive the border color style for the localization view. Why: Theme and state dependent styling should be calculated from current UI context. */
              borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.15)',
              color: 'text.primary',
              /** Purpose: Derive the bgcolor style for the localization view. Why: Theme and state dependent styling should be calculated from current UI context. */
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#ffffff',
              '&:hover': {
                borderColor: 'primary.main',
                /** Purpose: Derive the bgcolor style for the localization view. Why: Theme and state dependent styling should be calculated from current UI context. */
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
              },
            }}
          >
            Auto translate blanks
          </Button>
          {hasUnsavedChanges && (
            <Chip
              size="small"
              color="warning"
              label="Unsaved"
              sx={{ fontWeight: 700, height: 26, fontSize: '0.75rem' }}
            />
          )}
          {saveButton}
        </Box>
        </Portal>

      <StatusNotification message={message} onClose={() => setMessage(null)} />

      {selectedEnv === 'prod' && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2.5 }}>
          Production localization is read-only. Edit and save translations in DEV, then deploy them with the bot from Release Center.
        </Alert>
      )}

      <Dialog open={languagesDialogOpen} onClose={() => setLanguagesDialogOpen(false)} maxWidth="sm" fullWidth aria-labelledby="localization-languages-title" PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle id="localization-languages-title">Manage languages</DialogTitle>
        <DialogContent>
        {selectedEnv === 'prod' && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            In PROD, enabled languages are read-only and automatically mirror the languages selected in DEV.
          </Alert>
        )}
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5} mb={1}>
            <Box>
                <Chip
                  size="small"
                  color="primary"
                  label={`${enabled.length} enabled / ${allowedLanguages.length} allowed`}
                  sx={{ fontWeight: 700, fontSize: '0.75rem' }}
                />
              <Typography variant="body2" color="text.secondary" sx={{ my: 1.5 }}>
                Choose your bot’s languages and default. Changes are saved with Save localization.
              </Typography>
            </Box>

          <Box minWidth={220}>
            <TextField
              select disabled={!canEdit} fullWidth
              size="small"
              label="Default language"
              value={data.defaultLanguage}
              onChange={(event) => {
                setData((current) => ({
                  ...current,
                  defaultLanguage: event.target.value,
                  enabledLanguages: Array.from(new Set([...current.enabledLanguages, event.target.value])),
                }));
                setHasUnsavedChanges(true);
              }}
              SelectProps={{ native: true }}
              sx={{
                /** Purpose: Derive the bgcolor style for the localization view. Why: Theme and state dependent styling should be calculated from current UI context. */
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#fafafa',
                borderRadius: 2,
                '& .MuiOutlinedInput-root': { borderRadius: 2 },
              }}
            >
              {enabled.map(([code, label]) => (
                <option key={code} value={code}>
                  ⭐ {label} ({code})
                </option>
              ))}
            </TextField>
          </Box>
        </Box>

        <Divider sx={{ my: 1.25 }} />

        <Box display="flex" flexWrap="wrap" gap={1.2}>
          {LANGUAGES.filter(([code]) => allowedLanguages.includes(code) || data.enabledLanguages.includes(code)).map(([code, label]) => {
            const isEnabled = data.enabledLanguages.includes(code);
            const isDefault = code === data.defaultLanguage;
            return (
              <Box
                key={code}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  border: '1.5px solid',
                  borderColor: isEnabled ? 'primary.main' : 'divider',
                  bgcolor: isEnabled
                    ? (theme) => theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.12)' : 'rgba(59, 130, 246, 0.05)'
                    : 'transparent',
                  borderRadius: 999,
                  px: 1.5,
                  py: 0.3,
                  transition: 'all 0.15s ease-in-out',
                }}
              >
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={isEnabled}
                      disabled={!canEdit || code === 'en' || isDefault}
                      onChange={() => toggleLanguage(code)}
                      sx={{ p: 0.5, color: isEnabled ? 'primary.main' : 'text.disabled' }}
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={0.8} sx={{ userSelect: 'none' }}>
                      <Typography variant="body2" fontWeight={isEnabled ? 700 : 500} color={isEnabled ? 'text.primary' : 'text.secondary'}>
                        {label}
                      </Typography>
                      <Chip
                        size="small"
                        label={code}
                        sx={{
                          height: 20,
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          bgcolor: isEnabled ? 'primary.main' : 'action.selected',
                          color: isEnabled ? '#fff' : 'text.secondary',
                        }}
                      />
                      {isDefault && (
                        <Chip
                          size="small"
                          label="Default"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.65rem', fontWeight: 800, px: 0.2 }}
                        />
                      )}
                    </Box>
                  }
                />
              </Box>
            );
          })}
        </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLanguagesDialogOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* Translation Keys Header & Controls */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.75, md: 2 },
          borderRadius: '16px',
          border: '1px solid',
          borderColor: 'divider',
          /** Purpose: Resolve background for the localization module. Why: The surrounding operation uses this value to make its decision. */
          background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#ffffff',
          boxShadow: '0 12px 30px -18px rgba(15,23,42,0.28)',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5} mb={1.5}>
          <Box>
            <Box display="flex" alignItems="center" gap={1.2} flexWrap="wrap">
              <Typography component="h1" variant="h6" fontWeight={800}>
                Translation keys
              </Typography>
              <Chip
                size="small"
                label={`${filteredEntries.length} ${filteredEntries.length === 1 ? 'key' : 'keys'}`}
                sx={{ fontWeight: 700 }}
              />
              {hasUnsavedChanges && (
                <Chip
                  size="small"
                  color="warning"
                  label="Unsaved changes"
                  sx={{ fontWeight: 700 }}
                />
              )}
              <Box role="group" aria-label="Enabled languages" display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                {enabled.map(([code, label]) => (
                  <Chip
                    key={code}
                    size="small"
                    color="primary"
                    variant="outlined"
                    icon={<CheckCircleRoundedIcon />}
                    label={`${label} (${code})${code === data.defaultLanguage ? ' · Default' : ''}`}
                    sx={{ fontWeight: 600 }}
                  />
                ))}
              </Box>
              <Button size="small" onClick={() => setLanguagesDialogOpen(true)} disabled={loading} sx={{ textTransform: 'none', borderRadius: '8px' }}>Manage languages</Button>
              {missingCount > 0 && (
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  icon={<WarningAmberRoundedIcon fontSize="small" />}
                  label={`${missingCount} missing translations`}
                  sx={{ fontWeight: 700, fontSize: '0.75rem' }}
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
              Adding a language creates a new column for every existing key. Blank values safely fall back to the default language.
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, next) => next && setViewMode(next)}
              size="small"
              sx={{
                height: 34,
                '& .MuiToggleButton-root': {
                  px: 1.5,
                  py: 0.5,
                  borderRadius: '8px',
                  textTransform: 'none',
                },
              }}
            >
              <ToggleButton value="card" aria-label="Card view">
                <Tooltip title="Spacious Card View">
                  <Box display="flex" alignItems="center" gap={0.8} px={0.5}>
                    <GridViewRoundedIcon sx={{ fontSize: 16 }} />
                    <Typography variant="caption" fontWeight={700}>Cards</Typography>
                  </Box>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="table" aria-label="Table view">
                <Tooltip title="Compact Table View">
                  <Box display="flex" alignItems="center" gap={0.8} px={0.5}>
                    <TableRowsRoundedIcon sx={{ fontSize: 16 }} />
                    <Typography variant="caption" fontWeight={700}>Table</Typography>
                  </Box>
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>

            <Button
              variant="contained"
              color="primary"
              startIcon={<AddRoundedIcon sx={{ fontSize: 18 }} />}
              onClick={addEntry}
              disabled={!canEdit}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 700,
                px: 2,
                height: 34,
                fontSize: '0.8125rem',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
              }}
            >
              Add key
            </Button>
            {!headerHost && saveButton}
          </Box>
        </Box>

        {/* Search & Filter Bar */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          flexWrap="wrap"
          mb={2}
          sx={{
            p: 1.5,
            borderRadius: 2.5,
            /** Purpose: Derive the bgcolor style for the localization view. Why: Theme and state dependent styling should be calculated from current UI context. */
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <TextField
            size="small"
            placeholder="Search by key name or translation text..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')}>
                    <CloseRoundedIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              flex: { xs: '1 1 100%', sm: '1 1 320px' },
              maxWidth: 480,
              /** Purpose: Derive the bgcolor style for the localization view. Why: Theme and state dependent styling should be calculated from current UI context. */
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : '#ffffff',
              borderRadius: 2,
              '& .MuiOutlinedInput-root': { borderRadius: 2 },
            }}
          />

          <Box display="flex" gap={1} alignItems="center">
            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', mr: 0.5 }}>
              Filter:
            </Typography>
            <Chip
              size="small"
              clickable
              variant={statusFilter === 'all' ? 'filled' : 'outlined'}
              color={statusFilter === 'all' ? 'primary' : 'default'}
              label={`All (${totalKeys})`}
              onClick={() => setStatusFilter('all')}
              sx={{ fontWeight: 600 }}
            />
            <Chip
              size="small"
              clickable
              variant={statusFilter === 'missing' ? 'filled' : 'outlined'}
              color={statusFilter === 'missing' ? 'warning' : 'default'}
              label={`Missing (${missingCount})`}
              onClick={() => setStatusFilter('missing')}
              sx={{ fontWeight: 600 }}
            />
            <Chip
              size="small"
              clickable
              variant={statusFilter === 'complete' ? 'filled' : 'outlined'}
              color={statusFilter === 'complete' ? 'success' : 'default'}
              label={`Complete (${totalKeys - missingCount})`}
              onClick={() => setStatusFilter('complete')}
              sx={{ fontWeight: 600 }}
            />
          </Box>
        </Box>

        {loading ? (
          <Box p={8} textAlign="center">
            <CircularProgress size={36} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading localization keys...
            </Typography>
          </Box>
        ) : filteredEntries.length === 0 ? (
          <Box
            py={8}
            px={3}
            textAlign="center"
            sx={{
              borderRadius: 3,
              border: '1.5px dashed',
              borderColor: 'divider',
              /** Purpose: Derive the bgcolor style for the localization view. Why: Theme and state dependent styling should be calculated from current UI context. */
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.01)' : 'rgba(0,0,0,0.01)',
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                bgcolor: 'action.hover',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
                mb: 1.5,
              }}
            >
              <TranslateRoundedIcon />
            </Box>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              {totalKeys === 0 ? 'No localization keys yet' : 'No matching keys found'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 440, mx: 'auto', mb: 2.5 }}>
              {totalKeys === 0
                ? 'Create reusable keys for messages, menus, and bot prompts. Map them inside the flow canvas.'
                : 'Try adjusting your search query or clear the filter to view all keys.'}
            </Typography>
            {totalKeys === 0 ? (
              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={addEntry}
                disabled={!canEdit}
                sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700 }}
              >
                Add key
              </Button>
            ) : (
              <Button
                variant="outlined"
                onClick={() => { setSearchQuery(''); setStatusFilter('all'); }}
                sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 600 }}
              >
                Reset filter
              </Button>
            )}
          </Box>
        ) : viewMode === 'card' ? (
          /* ========================================================================= */
          /* CARD VIEW: Spacious layout with dedicated key header & huge value editors */
          /* ========================================================================= */
          <Box display="flex" flexDirection="column" gap={3}>
            {filteredEntries.map(({ entry, originalIndex }) => {
              /** Purpose: Perform is key complete for the localization module. Why: Callers need a consistent result from this shared operation. */
              const isKeyComplete = enabled.every(([code]) => Boolean(String(entry.values?.[code] || '').trim()));
              /** Purpose: Resolve missing languages for the localization module. Why: The surrounding operation uses this value to make its decision. */
              const missingLanguages = enabled.filter(([code]) => !String(entry.values?.[code] || '').trim());

              return (
                <Card
                  key={entry._rowId}
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    border: '1.5px solid',
                    borderColor: 'divider',
                    boxShadow: '0 4px 16px -4px rgba(0,0,0,0.05)',
                    overflow: 'visible',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: '0 6px 20px -4px rgba(59, 130, 246, 0.1)',
                    },
                  }}
                >
                  {/* Key Header Bar */}
                  <Box
                    sx={{
                      p: { xs: 2, sm: 2.5 },
                      /** Purpose: Derive the bgcolor style for the localization view. Why: Theme and state dependent styling should be calculated from current UI context. */
                      bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 2,
                          bgcolor: 'primary.main',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 800,
                          fontSize: '0.8rem',
                        }}
                      >
                        #{originalIndex + 1}
                      </Box>

                      {/* Monospace Key Field with Generous Space */}
                      <Box sx={{ minWidth: { xs: '100%', sm: 360, md: 440 }, maxWidth: 640 }}>
                        <TextField
                          size="small"
                          fullWidth
                          value={entry.key}
                          disabled={!canEdit}
                          onChange={(event) =>
                            updateEntry(originalIndex, {
                              key: event.target.value.toLowerCase().replace(/[^a-z0-9_.-]/g, ''),
                            })
                          }
                          placeholder="welcome_message"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <TagRoundedIcon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }} />
                              </InputAdornment>
                            ),
                            endAdornment: entry.key ? (
                              <InputAdornment position="end">
                                <Tooltip title={copiedKeyIndex === originalIndex ? 'Copied key!' : 'Copy key'}>
                                  <IconButton
                                    size="small"
                                    onClick={() => copyKeyToClipboard(entry.key, originalIndex)}
                                    color={copiedKeyIndex === originalIndex ? 'success' : 'default'}
                                  >
                                    <ContentCopyRoundedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            ) : null,
                          }}
                          sx={{
                            '& .MuiInputBase-input': {
                              fontFamily: 'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
                              fontWeight: 700,
                              fontSize: '0.92rem',
                              letterSpacing: '0.02em',
                              color: 'primary.main',
                            },
                            /** Purpose: Derive the bgcolor style for the localization view. Why: Theme and state dependent styling should be calculated from current UI context. */
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : '#ffffff',
                            borderRadius: 2,
                            '& .MuiOutlinedInput-root': { borderRadius: 2 },
                          }}
                        />
                      </Box>

                      {/* Status indicator badge */}
                      {isKeyComplete ? (
                        <Chip
                          size="small"
                          color="success"
                          icon={<CheckCircleRoundedIcon fontSize="small" />}
                          label="Complete"
                          sx={{ fontWeight: 700, height: 26, fontSize: '0.75rem' }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          color="warning"
                          variant="outlined"
                          icon={<WarningAmberRoundedIcon fontSize="small" />}
                          label={`Missing: ${missingLanguages.map(([, l]) => l).join(', ')}`}
                          sx={{ fontWeight: 700, height: 26, fontSize: '0.75rem' }}
                        />
                      )}
                    </Box>

                    {/* Delete Action */}
                    <Box display="flex" alignItems="center" gap={1}>
                      <Tooltip title="Delete this key and all translations">
                        <IconButton
                          color="error"
                          disabled={!canEdit}
                          onClick={() => deleteEntry(originalIndex)}
                          sx={{
                            borderRadius: 2,
                            /** Purpose: Derive the bgcolor style for the localization view. Why: Theme and state dependent styling should be calculated from current UI context. */
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)',
                            '&:hover': {
                              bgcolor: 'error.main',
                              color: '#fff',
                            },
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {/* Values Body: Spacious multi-column editor */}
                  <Box
                    sx={{
                      p: { xs: 2, sm: 2.5 },
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        lg: enabled.length > 1 ? `repeat(${Math.min(enabled.length, 3)}, minmax(340px, 1fr))` : '1fr',
                      },
                      gap: 2.5,
                      /** Purpose: Derive the bgcolor style for the localization view. Why: Theme and state dependent styling should be calculated from current UI context. */
                      bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.1)' : '#ffffff',
                    }}
                  >
                    {enabled.map(([code, label]) => {
                      const isDefault = code === data.defaultLanguage;
                      const currentValue = entry.values?.[code] || '';
                      const charCount = currentValue.length;
                      const lineCount = currentValue ? currentValue.split('\n').length : 0;
                      const defaultValue = entry.values?.[data.defaultLanguage] || '';

                      return (
                        <Box
                          key={code}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            p: 2,
                            borderRadius: 2.5,
                            border: '1px solid',
                            borderColor: isDefault ? 'primary.light' : 'divider',
                            bgcolor: isDefault
                              ? (theme) => theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.04)' : 'rgba(59, 130, 246, 0.015)'
                              : (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.015)' : '#fafbfc',
                          }}
                        >
                          {/* Language Header in Box */}
                          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="subtitle2" fontWeight={800} color={isDefault ? 'primary.main' : 'text.primary'}>
                                {label}
                              </Typography>
                              <Chip
                                size="small"
                                label={code}
                                sx={{
                                  height: 20,
                                  fontSize: '0.7rem',
                                  fontWeight: 700,
                                  bgcolor: isDefault ? 'primary.main' : 'action.selected',
                                  color: isDefault ? '#fff' : 'text.secondary',
                                }}
                              />
                              {isDefault && (
                                <Chip
                                  size="small"
                                  label="Default"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: '0.65rem', fontWeight: 800 }}
                                />
                              )}
                            </Box>

                            <Box display="flex" alignItems="center" gap={1}>
                              {!isDefault && !currentValue && defaultValue && (
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => copyDefaultValueToLanguage(originalIndex, code)}
                                  disabled={!canEdit}
                                  sx={{
                                    textTransform: 'none',
                                    fontSize: '0.72rem',
                                    p: 0.3,
                                    minWidth: 0,
                                    fontWeight: 700,
                                    color: 'primary.main',
                                  }}
                                >
                                  Copy from default
                                </Button>
                              )}
                              {charCount > 0 && (
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                  {charCount} chars • {lineCount} {lineCount === 1 ? 'line' : 'lines'}
                                </Typography>
                              )}
                            </Box>
                          </Box>

                          {/* Generous multiline textarea */}
                          <TextField
                            fullWidth
                            multiline
                            rows={2}
                            InputProps={{ inputComponent: 'textarea' }}
                            inputProps={{ rows: 2, 'aria-label': `${entry.key || 'New key'} — ${label}` }}
                            value={currentValue}
                            disabled={!canEdit}
                            onChange={(event) => updateValue(originalIndex, code, event.target.value)}
                            placeholder={
                              isDefault
                                ? 'Required default text. Markdown, line breaks, and emojis are supported.'
                                : `Enter ${label} translation. If left blank, it safely falls back to ${data.defaultLanguage}.`
                            }
                            sx={{
                              '& textarea': { resize: 'vertical !important', minHeight: '42px', overflow: 'auto !important' },
                              /** Purpose: Derive the bgcolor style for the localization view. Why: Theme and state dependent styling should be calculated from current UI context. */
                              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : '#ffffff',
                              borderRadius: 2,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                p: 1.5,
                                fontSize: '0.92rem',
                                lineHeight: 1.6,
                              },
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </Card>
              );
            })}

          </Box>
        ) : (
          /* ========================================================================= */
          /* TABLE VIEW: Compact spreadsheet grid with wide columns & horizontal scroll*/
          /* ========================================================================= */
          <Box sx={{ overflowX: 'auto', pb: 2 }}>
            <Box sx={{ minWidth: Math.max(900, 320 + enabled.length * 360) }}>
              <Box
                display="grid"
                gridTemplateColumns={`280px ${enabled.map(() => 'minmax(320px, 1fr)').join(' ')} 56px`}
                gap={2}
                py={1.5}
                px={2}
                mb={1}
                sx={{
                  /** Purpose: Derive the bgcolor style for the localization view. Why: Theme and state dependent styling should be calculated from current UI context. */
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f1f5f9',
                  borderRadius: 2,
                  alignItems: 'center',
                }}
              >
                <Typography fontWeight={800} variant="subtitle2">
                  Key
                </Typography>
                {enabled.map(([code, label]) => (
                  <Box key={code} display="flex" alignItems="center" gap={1}>
                    <Typography fontWeight={800} variant="subtitle2">
                      {label}
                    </Typography>
                    <Chip size="small" label={code} sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }} />
                    {code === data.defaultLanguage && (
                      <Chip size="small" label="Default" color="primary" sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                  </Box>
                ))}
                <span />
              </Box>

              {filteredEntries.map(({ entry, originalIndex }) => (
                <Box
                  key={entry._rowId}
                  display="grid"
                  gridTemplateColumns={`280px ${enabled.map(() => 'minmax(320px, 1fr)').join(' ')} 56px`}
                  gap={2}
                  mb={0.75}
                  p={1}
                  sx={{
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    /** Purpose: Derive the bgcolor style for the localization view. Why: Theme and state dependent styling should be calculated from current UI context. */
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : '#ffffff',
                    alignItems: 'start',
                  }}
                >
                  <TextField
                    size="small"
                    value={entry.key}
                    disabled={!canEdit}
                    onChange={(event) =>
                      updateEntry(originalIndex, {
                        key: event.target.value.toLowerCase().replace(/[^a-z0-9_.-]/g, ''),
                      })
                    }
                    placeholder="welcome_message"
                    sx={{
                      '& .MuiInputBase-input': {
                        fontFamily: 'monospace',
                        fontWeight: 700,
                        fontSize: '0.88rem',
                        color: 'primary.main',
                      },
                    }}
                  />
                  {enabled.map(([code, label]) => (
                    <TextField
                      key={code}
                      size="small"
                      multiline
                      rows={1}
                      InputProps={{ inputComponent: 'textarea' }}
                      inputProps={{ rows: 1, 'aria-label': `${entry.key || 'New key'} — ${label}` }}
                      value={entry.values?.[code] || ''}
                      disabled={!canEdit}
                      onChange={(event) => updateValue(originalIndex, code, event.target.value)}
                      placeholder={
                        code === data.defaultLanguage
                          ? 'Required default value'
                          : `Fallback: ${data.defaultLanguage}`
                      }
                      sx={{
                        '& textarea': { resize: 'vertical !important', minHeight: '24px', overflow: 'auto !important' },
                        lineHeight: 1.5,
                        '& .MuiOutlinedInput-root': { fontSize: '0.88rem' },
                      }}
                    />
                  ))}
                  <IconButton
                    color="error"
                    disabled={!canEdit}
                    onClick={() => deleteEntry(originalIndex)}
                    sx={{ mt: 0.5 }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}

            </Box>
          </Box>
        )}
        {!loading && filteredEntries.length > 0 && (
          <Box role="toolbar" aria-label="Translation key actions" display="flex" justifyContent="flex-end" flexWrap="wrap" gap={1} sx={{ pt: 2, mt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button variant="outlined" startIcon={<AddRoundedIcon sx={{ fontSize: 18 }} />} onClick={addEntry} disabled={!canEdit} sx={{ borderRadius: '10px', height: 34, fontSize: '0.8125rem', textTransform: 'none', fontWeight: 700 }}>
              Add key
            </Button>
            {saveButton}
          </Box>
        )}
      </Paper>

      {/* Import Modal Dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3.5, p: { xs: 1, sm: 1.5 } } }}
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box display="flex" alignItems="center" gap={1.2}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2.5,
                bgcolor: 'primary.main',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FileUploadRoundedIcon fontSize="small" />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={800} lineHeight={1.2}>
                Import Localization
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Upload JSON or CSV translation bundle
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={() => setImportDialogOpen(false)}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, py: 2.5 }}>
          <input
            type="file"
            ref={fileInputRef}
            accept=".json,.csv,text/csv,application/json"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          <Box
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const file = e.dataTransfer?.files?.[0];
              if (file) processFile(file);
            }}
            sx={{
              border: '2px dashed',
              borderColor: importError ? 'error.main' : importPreview ? 'success.main' : 'divider',
              borderRadius: 3,
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              /** Purpose: Derive the bgcolor style for the localization view. Why: Theme and state dependent styling should be calculated from current UI context. */
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#f8fafc',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: 'primary.main',
                /** Purpose: Derive the bgcolor style for the localization view. Why: Theme and state dependent styling should be calculated from current UI context. */
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#f1f5f9',
              },
            }}
          >
            <UploadFileRoundedIcon sx={{ fontSize: 44, color: importPreview ? 'success.main' : 'primary.main', mb: 1 }} />
            <Typography variant="subtitle1" fontWeight={700}>
              {importFile ? importFile.name : 'Click to select or drag & drop file'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Supports .json or .csv translation files
            </Typography>
            {importFile && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {(importFile.size / 1024).toFixed(1)} KB
              </Typography>
            )}
          </Box>

          {importError && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {importError}
            </Alert>
          )}

          {importPreview && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  /** Purpose: Derive the bgcolor style for the localization view. Why: Theme and state dependent styling should be calculated from current UI context. */
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#fdfdfe',
                }}
              >
                <Typography variant="caption" fontWeight={800} color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>
                  Detected File Summary
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                  <Chip
                    size="small"
                    icon={<CheckCircleRoundedIcon fontSize="small" />}
                    color="success"
                    variant="outlined"
                    label={`${importPreview.validCount} valid ${importPreview.validCount === 1 ? 'key' : 'keys'}`}
                    sx={{ fontWeight: 700 }}
                  />
                  {importPreview.invalidCount > 0 && (
                    <Chip
                      size="small"
                      icon={<WarningAmberRoundedIcon fontSize="small" />}
                      color="warning"
                      variant="outlined"
                      label={`${importPreview.invalidCount} invalid/skipped`}
                      sx={{ fontWeight: 700 }}
                    />
                  )}
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${importPreview.detectedLangs.length} languages: ${importPreview.detectedLangs.join(', ') || 'none'}`}
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              </Paper>

              <Box>
                <Typography variant="subtitle2" fontWeight={800} mb={1}>
                  Import Strategy
                </Typography>
                <RadioGroup
                  value={importMode}
                  onChange={(e) => setImportMode(e.target.value)}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      mb: 1.2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: importMode === 'merge' ? 'primary.main' : 'divider',
                      bgcolor: importMode === 'merge' ? (theme) => theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.08)' : 'rgba(25, 118, 210, 0.04)' : 'transparent',
                    }}
                  >
                    <FormControlLabel
                      value="merge"
                      control={<Radio size="small" />}
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={700}>
                            Merge & Update (Recommended)
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Updates matching keys with values from the file and appends any new keys. Existing keys not in the file are preserved.
                          </Typography>
                        </Box>
                      }
                      sx={{ m: 0, alignItems: 'flex-start' }}
                    />
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: importMode === 'replace' ? 'error.main' : 'divider',
                      bgcolor: importMode === 'replace' ? (theme) => theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.08)' : 'rgba(211, 47, 47, 0.04)' : 'transparent',
                    }}
                  >
                    <FormControlLabel
                      value="replace"
                      control={<Radio size="small" color="error" />}
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={700}>
                            Replace All Keys
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Clears all current keys and replaces them completely with the keys from the imported file.
                          </Typography>
                        </Box>
                      }
                      sx={{ m: 0, alignItems: 'flex-start' }}
                    />
                  </Paper>
                </RadioGroup>
              </Box>

              {importPreview.detectedLangs.some((lang) => allowedLanguages.includes(lang) && !data.enabledLanguages.includes(lang)) && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={autoEnableLanguages}
                      onChange={(e) => setAutoEnableLanguages(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Automatically enable new detected languages for this bot
                    </Typography>
                  }
                />
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setImportDialogOpen(false)}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={applyImport}
            disabled={!importPreview || !importPreview.validCount}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 2.5 }}
          >
            Apply import ({importPreview ? importPreview.validCount : 0} keys)
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
